/**
 * <lookbook-scene> — progressive enhancement for shop-the-look hotspots.
 *
 * The hotspots use the native popover API (button[popovertarget] + [popover]),
 * so they already open, close on Escape, light-dismiss, and stay keyboard
 * accessible with zero JS. This component adds:
 *   1. Within a scene, opening one hotspot card closes any other that is open.
 *   2. Place the open card next to its + invoker (clamped inside the viewport).
 * If the browser lacks popover support the dots do nothing harmful — they
 * remain focusable buttons.
 */
class LookbookScene extends HTMLElement {
  /** @type {HTMLElement[]} */
  #cards = [];

  connectedCallback() {
    this.#cards = Array.from(this.querySelectorAll('[popover]'));
    if (!this.#cards.length) return;

    for (const card of this.#cards) {
      card.addEventListener('beforetoggle', this.#handleBeforeToggle);
      card.addEventListener('toggle', this.#handleToggle);
    }
  }

  disconnectedCallback() {
    for (const card of this.#cards) {
      card.removeEventListener('beforetoggle', this.#handleBeforeToggle);
      card.removeEventListener('toggle', this.#handleToggle);
    }
  }

  /**
   * When a card is about to open, close every sibling that is currently open.
   * @param {ToggleEvent} event
   */
  #handleBeforeToggle = (event) => {
    if (event.newState !== 'open') return;
    const opening = /** @type {HTMLElement} */ (event.target);

    for (const card of this.#cards) {
      if (card === opening) continue;
      if (card.matches(':popover-open')) card.hidePopover();
    }

    this.dispatchEvent(
      new CustomEvent('lookbook:hotspot-open', { bubbles: true })
    );
  };

  /**
   * After open: pin the card beside its invoker.
   * @param {ToggleEvent} event
   */
  #handleToggle = (event) => {
    if (event.newState !== 'open') return;
    const card = /** @type {HTMLElement} */ (event.target);
    this.#positionNearInvoker(card);
  };

  /**
   * @param {HTMLElement} card
   */
  #positionNearInvoker(card) {
    const invoker =
      this.querySelector(`[popovertarget="${card.id}"]`) ||
      document.getElementById(`${card.id}-invoker`);
    if (!invoker) return;

    const gap = 8;
    const margin = 12;
    const invokerRect = invoker.getBoundingClientRect();

    // Measure after the popover is shown so width/height are real.
    const cardRect = card.getBoundingClientRect();
    const cardWidth = cardRect.width || 280;
    const cardHeight = cardRect.height || 96;
    const viewW = window.innerWidth;
    const viewH = window.innerHeight;

    let top = invokerRect.bottom + gap;
    let left = invokerRect.left + invokerRect.width / 2 - cardWidth / 2;

    // Prefer below; if it would clip the bottom, flip above the +.
    if (top + cardHeight > viewH - margin) {
      top = invokerRect.top - gap - cardHeight;
    }

    top = Math.min(Math.max(top, margin), Math.max(margin, viewH - cardHeight - margin));
    left = Math.min(Math.max(left, margin), Math.max(margin, viewW - cardWidth - margin));

    card.style.setProperty('--lookbook-card-top', `${Math.round(top)}px`);
    card.style.setProperty('--lookbook-card-left', `${Math.round(left)}px`);
    card.style.setProperty('--lookbook-card-translate', '0 0');
  }
}

if (!customElements.get('lookbook-scene')) {
  customElements.define('lookbook-scene', LookbookScene);
}
